// Per-method IDB cache mutators for the write policies.
//
// These run AFTER the rpc wrapper decides what to do (online success,
// offline queue) and patch the relevant read caches so the next read
// — whether from the network or from IDB — sees the change. Without
// them, queued-while-offline edits disappear the moment the user
// navigates to a different tab and the section's hook remounts +
// re-pulls from IDB.
//
// Each mutator is "best effort": it reads the current cached read
// response, applies the change, and writes it back. Cache misses
// (no prior online fetch) are no-ops — there's nothing to patch.
// The hook will still re-pull from network when online and the cache
// will populate normally.

import { localStore, type CacheEntry } from "./local-store";

type AnyRecord = Record<string, any>;

export type CacheMutator = (
  args: AnyRecord,
  response: AnyRecord,
) => Promise<void>;

// Look up a single cached read response and re-write it with the
// patched value. Use undefined-on-miss so callers can short-circuit.
async function patch<T extends AnyRecord>(
  store: string,
  key: string,
  fn: (current: T | null) => T | null,
): Promise<void> {
  const cached = await localStore.get<T>(store, key).catch(() => undefined);
  const next = fn(cached?.value ?? null);
  if (next === null) {
    // Caller is deleting; remove the entry.
    await localStore.delete(store, key).catch(() => {});
    return;
  }
  await localStore.put<T>(store, {
    key,
    value: next,
    fetchedAt: Date.now(),
  } as CacheEntry<T>).catch(() => {});
}

// Cross-store scan: find which entry contains an item with the given
// id, apply the mutator, write back. Used for update/delete where the
// caller doesn't pass the parent listId (e.g. shopping.updateItem
// only carries itemId + patch).
async function scanAndPatch<TItem extends { id: any }>(
  store: string,
  itemId: any,
  itemsKey: string, // e.g. "items"
  fn: (item: TItem, items: TItem[], idx: number) => TItem[] | null,
): Promise<void> {
  const all = await localStore.getAll<AnyRecord>(store).catch(() => [] as CacheEntry<AnyRecord>[]);
  for (const entry of all) {
    const items: TItem[] = entry.value?.[itemsKey] || [];
    const idx = items.findIndex((i) => String(i.id) === String(itemId));
    if (idx < 0) continue;
    const replacement = fn(items[idx], items.slice(), idx);
    if (replacement === null) return; // mutator decided not to write
    await localStore.put(store, {
      key: entry.key,
      value: { ...entry.value, [itemsKey]: replacement },
      fetchedAt: Date.now(),
    }).catch(() => {});
    return;
  }
}

// ─────────────────────────────────────────────────────────────────
// Mutator table — keyed by RPC method name. cache-policy.ts plugs
// these in via lookupMutator().
// ─────────────────────────────────────────────────────────────────

export const CACHE_MUTATORS: Record<string, CacheMutator> = {
  // ── Shopping ───────────────────────────────────────────────────
  "shopping.addItem": async (args, resp) => {
    const key = String(args.listId);
    await patch<AnyRecord>("shopping_items", key, (cur) => {
      const items = cur?.items || [];
      return { ...(cur || { ok: true }), items: [...items, resp.item] };
    });
  },
  "shopping.updateItem": async (args, resp) => {
    await scanAndPatch("shopping_items", args.itemId, "items", (existing, items, idx) => {
      items[idx] = { ...existing, ...resp.item };
      return items;
    });
  },
  "shopping.deleteItem": async (args) => {
    await scanAndPatch("shopping_items", args.itemId, "items", (_existing, items, idx) => {
      items.splice(idx, 1);
      return items;
    });
  },
  "shopping.clearPurchased": async (args) => {
    const key = String(args.listId);
    await patch<AnyRecord>("shopping_items", key, (cur) => {
      if (!cur) return null;
      const items = (cur.items || []).filter((i: AnyRecord) => !i.purchased);
      return { ...cur, items };
    });
  },
  "shopping.createList": async (_args, resp) => {
    await patch<AnyRecord>("shopping_lists", "_", (cur) => {
      const lists = cur?.lists || [];
      return { ...(cur || { ok: true }), lists: [...lists, resp.list] };
    });
  },
  "shopping.updateList": async (args, resp) => {
    await patch<AnyRecord>("shopping_lists", "_", (cur) => {
      if (!cur) return null;
      const lists = (cur.lists || []).map((l: AnyRecord) =>
        String(l.id) === String(args.listId) ? { ...l, ...resp.list } : l);
      return { ...cur, lists };
    });
  },
  "shopping.deleteList": async (args) => {
    await patch<AnyRecord>("shopping_lists", "_", (cur) => {
      if (!cur) return null;
      const lists = (cur.lists || []).filter((l: AnyRecord) =>
        String(l.id) !== String(args.listId));
      return { ...cur, lists };
    });
    // Items in the deleted list are now orphaned; drop their cache.
    await localStore.delete("shopping_items", String(args.listId)).catch(() => {});
  },

  // ── To-do ──────────────────────────────────────────────────────
  "todos.addItem": async (args, resp) => {
    const key = String(args.listId);
    await patch<AnyRecord>("todos_items", key, (cur) => {
      const items = cur?.items || [];
      return { ...(cur || { ok: true }), items: [...items, resp.item] };
    });
  },
  "todos.updateItem": async (args, resp) => {
    await scanAndPatch("todos_items", args.itemId, "items", (existing, items, idx) => {
      items[idx] = { ...existing, ...resp.item };
      return items;
    });
  },
  "todos.deleteItem": async (args) => {
    await scanAndPatch("todos_items", args.itemId, "items", (_existing, items, idx) => {
      items.splice(idx, 1);
      return items;
    });
  },
  "todos.clearDone": async (args) => {
    const key = String(args.listId);
    await patch<AnyRecord>("todos_items", key, (cur) => {
      if (!cur) return null;
      const items = (cur.items || []).filter((t: AnyRecord) => !t.done);
      return { ...cur, items };
    });
  },

  // ── Meal plan ──────────────────────────────────────────────────
  // The cache is keyed by `${from}|${to}`. We touch every range that
  // includes the affected date so navigating between weeks doesn't
  // hide a freshly-queued change.
  "mealplan.set": async (args, resp) => {
    const all = await localStore.getAll<AnyRecord>("mealplan_entries").catch(() => [] as CacheEntry<AnyRecord>[]);
    for (const entry of all) {
      const [from, to] = entry.key.split("|");
      if (!from || !to) continue;
      if (args.date < from || args.date > to) continue;
      const entries: AnyRecord[] = entry.value?.entries || [];
      const idx = entries.findIndex((e) => e.date === args.date && e.slot === args.slot);
      const next = entries.slice();
      if (idx >= 0) next[idx] = { ...next[idx], ...resp.entry };
      else next.push(resp.entry);
      await localStore.put("mealplan_entries", {
        key: entry.key,
        value: { ...entry.value, entries: next },
        fetchedAt: Date.now(),
      }).catch(() => {});
    }
  },
  "mealplan.clear": async (args) => {
    const all = await localStore.getAll<AnyRecord>("mealplan_entries").catch(() => [] as CacheEntry<AnyRecord>[]);
    for (const entry of all) {
      const entries: AnyRecord[] = entry.value?.entries || [];
      const filtered = entries.filter((e) => !(e.date === args.date && e.slot === args.slot));
      if (filtered.length === entries.length) continue;
      await localStore.put("mealplan_entries", {
        key: entry.key,
        value: { ...entry.value, entries: filtered },
        fetchedAt: Date.now(),
      }).catch(() => {});
    }
  },

  // ── Notes ──────────────────────────────────────────────────────
  "notes.save": async (args, resp) => {
    // Update the body cache so a re-read shows the queued content.
    await patch<AnyRecord>("notes_files", `${args.root}:${args.path}`, (cur) => ({
      ok: true,
      kind: cur?.kind || "markdown",
      content: args.content,
      sha256: resp.sha256 || cur?.sha256 || null,
      mtime: resp.mtime || Date.now(),
    }));
    // Patch the parent directory's cached listing so a newly-created
    // note appears in the list while offline. Server-side a save IS
    // a create when the file doesn't exist; we mirror that here.
    // For overwrites of existing files this is a no-op (the entry
    // already exists; we just bump mtime).
    const parent = args.path.includes("/") ? args.path.slice(0, args.path.lastIndexOf("/")) : "";
    const name = args.path.split("/").pop();
    if (name) {
      await patch<AnyRecord>("notes_dirs", `${args.root}:${parent}`, (cur) => {
        if (!cur) return null; // no cached listing — nothing to patch
        const entries: AnyRecord[] = cur.entries || [];
        const idx = entries.findIndex((e) => e.name === name);
        const nowMs = resp.mtime || Date.now();
        if (idx >= 0) {
          const next = entries.slice();
          next[idx] = { ...next[idx], mtimeMs: nowMs };
          return { ...cur, entries: next };
        }
        return {
          ...cur,
          entries: [...entries, {
            name,
            type: "file",
            size: typeof args.content === "string" ? args.content.length : 0,
            mtimeMs: nowMs,
          }],
        };
      });
    }
  },
  "notes.delete": async (args) => {
    await localStore.delete("notes_files", `${args.root}:${args.path}`).catch(() => {});
    // Strip from the parent directory listing so the file list stops
    // showing the deleted note.
    const parent = args.path.includes("/") ? args.path.slice(0, args.path.lastIndexOf("/")) : "";
    await patch<AnyRecord>("notes_dirs", `${args.root}:${parent}`, (cur) => {
      if (!cur) return null;
      const name = args.path.split("/").pop();
      const entries = (cur.entries || []).filter((e: AnyRecord) => e.name !== name);
      return { ...cur, entries };
    });
  },
  "notes.rename": async (args) => {
    const oldKey = `${args.root}:${args.oldPath}`;
    const newKey = `${args.root}:${args.newPath}`;
    const cached = await localStore.get<AnyRecord>("notes_files", oldKey).catch(() => undefined);
    if (cached) {
      await localStore.put("notes_files", { ...cached, key: newKey }).catch(() => {});
      await localStore.delete("notes_files", oldKey).catch(() => {});
    }
  },

  // ── Calendar ───────────────────────────────────────────────────
  // Range cache: each entry is one fetched range. New events go into
  // every cached range whose window covers the event's start.
  "calendar.createEvent": async (_args, resp) => {
    const evt = resp.event;
    if (!evt) return;
    const all = await localStore.getAll<AnyRecord>("calendar_events").catch(() => [] as CacheEntry<AnyRecord>[]);
    for (const entry of all) {
      const [rangeStart, rangeEnd] = entry.key.split("|");
      if (!rangeStart || !rangeEnd) continue;
      // Lexicographic compare is correct for ISO timestamps with
      // matching offsets — that's what the server emits.
      if (evt.dtstart < rangeStart || evt.dtstart > rangeEnd) continue;
      const events: AnyRecord[] = entry.value?.events || [];
      if (events.some((e) => e.id === evt.id)) continue;
      await localStore.put("calendar_events", {
        key: entry.key,
        value: { ...entry.value, events: [...events, evt] },
        fetchedAt: Date.now(),
      }).catch(() => {});
    }
  },
  "calendar.deleteEvent": async (args) => {
    const all = await localStore.getAll<AnyRecord>("calendar_events").catch(() => [] as CacheEntry<AnyRecord>[]);
    for (const entry of all) {
      const events: AnyRecord[] = entry.value?.events || [];
      const filtered = events.filter((e) => String(e.id) !== String(args.id));
      if (filtered.length === events.length) continue;
      await localStore.put("calendar_events", {
        key: entry.key,
        value: { ...entry.value, events: filtered },
        fetchedAt: Date.now(),
      }).catch(() => {});
    }
  },

  // ── Recipes ────────────────────────────────────────────────────
  // recipes.list is keyed by "_" (singleton); recipes.get is keyed
  // by recipeId. Mutations need to patch both stores so a freshly-
  // created recipe shows in the list AND its detail view doesn't
  // 404 if opened before a list refresh.
  "recipes.create": async (_args, resp) => {
    if (!resp?.recipe) return;
    await patch<AnyRecord>("recipes_list", "_", (cur) => {
      const recipes = cur?.recipes || [];
      return { ...(cur || { ok: true }), recipes: [...recipes, resp.recipe] };
    });
    await patch<AnyRecord>("recipes_get", String(resp.recipe.id), () => ({
      ok: true, recipe: resp.recipe, items: [],
    }));
  },
  "recipes.createWithItems": async (args, resp) => {
    if (!resp?.recipe) return;
    const items = Array.isArray(args.items)
      ? args.items.map((it: AnyRecord, i: number) => ({
          id: `_pending_item_${i}`,
          recipeId: resp.recipe.id,
          text: it.text || "",
          quantity: it.quantity || "",
          position: i,
          category: null,
          emoji: null,
          _queued: true,
        }))
      : [];
    await patch<AnyRecord>("recipes_list", "_", (cur) => {
      const recipes = cur?.recipes || [];
      return { ...(cur || { ok: true }), recipes: [...recipes, { ...resp.recipe, itemCount: items.length }] };
    });
    await patch<AnyRecord>("recipes_get", String(resp.recipe.id), () => ({
      ok: true, recipe: resp.recipe, items,
    }));
  },
  "recipes.update": async (args, resp) => {
    const recipe = resp?.recipe || { id: args.recipeId, ...args };
    // List: patch the matching row in place.
    await patch<AnyRecord>("recipes_list", "_", (cur) => {
      if (!cur) return null;
      const recipes = (cur.recipes || []).map((r: AnyRecord) =>
        String(r.id) === String(args.recipeId) ? { ...r, ...recipe } : r
      );
      return { ...cur, recipes };
    });
    // Detail: merge the patch into the cached body.
    await patch<AnyRecord>("recipes_get", String(args.recipeId), (cur) => {
      if (!cur) return null;
      return { ...cur, recipe: { ...cur.recipe, ...recipe } };
    });
  },
  "recipes.setTags": async (args, resp) => {
    const tags = resp?.recipe?.tags ?? args.tags ?? [];
    await patch<AnyRecord>("recipes_list", "_", (cur) => {
      if (!cur) return null;
      const recipes = (cur.recipes || []).map((r: AnyRecord) =>
        String(r.id) === String(args.recipeId) ? { ...r, tags } : r
      );
      return { ...cur, recipes };
    });
    await patch<AnyRecord>("recipes_get", String(args.recipeId), (cur) => {
      if (!cur) return null;
      return { ...cur, recipe: { ...cur.recipe, tags } };
    });
  },
  "recipes.retag": async (args) => {
    // Clear local tags so the UI reflects the wipe immediately. The
    // classifier re-runs async server-side; the recipe:tags.updated
    // SSE event populates them again when it lands.
    await patch<AnyRecord>("recipes_list", "_", (cur) => {
      if (!cur) return null;
      const recipes = (cur.recipes || []).map((r: AnyRecord) =>
        String(r.id) === String(args.recipeId) ? { ...r, tags: [] } : r
      );
      return { ...cur, recipes };
    });
    await patch<AnyRecord>("recipes_get", String(args.recipeId), (cur) => {
      if (!cur) return null;
      return { ...cur, recipe: { ...cur.recipe, tags: [] } };
    });
  },
  "recipes.delete": async (args) => {
    await patch<AnyRecord>("recipes_list", "_", (cur) => {
      if (!cur) return null;
      return { ...cur, recipes: (cur.recipes || []).filter((r: AnyRecord) => String(r.id) !== String(args.recipeId)) };
    });
    await localStore.delete("recipes_get", String(args.recipeId)).catch(() => {});
  },
  "recipes.addItem": async (args, resp) => {
    if (!resp?.item) return;
    await patch<AnyRecord>("recipes_get", String(args.recipeId), (cur) => {
      if (!cur) return null;
      return { ...cur, items: [...(cur.items || []), resp.item] };
    });
    // Bump itemCount on the list row so cards/rows render the new total.
    await patch<AnyRecord>("recipes_list", "_", (cur) => {
      if (!cur) return null;
      const recipes = (cur.recipes || []).map((r: AnyRecord) =>
        String(r.id) === String(args.recipeId)
          ? { ...r, itemCount: (r.itemCount || 0) + 1 }
          : r
      );
      return { ...cur, recipes };
    });
  },
  "recipes.updateItem": async (args, resp) => {
    if (!resp?.item) return;
    // Item carries recipeId in the response — use it to target the
    // right cached detail entry.
    const recipeId = resp.item.recipeId;
    if (recipeId == null) return;
    await patch<AnyRecord>("recipes_get", String(recipeId), (cur) => {
      if (!cur) return null;
      return {
        ...cur,
        items: (cur.items || []).map((it: AnyRecord) =>
          String(it.id) === String(args.itemId) ? { ...it, ...resp.item } : it
        ),
      };
    });
  },
  "recipes.deleteItem": async (args) => {
    // No recipeId in args — scan all cached recipe details for the item.
    const all = await localStore.getAll<AnyRecord>("recipes_get").catch(() => [] as CacheEntry<AnyRecord>[]);
    for (const entry of all) {
      const items: AnyRecord[] = entry.value?.items || [];
      const idx = items.findIndex((it) => String(it.id) === String(args.itemId));
      if (idx < 0) continue;
      const next = items.slice();
      next.splice(idx, 1);
      await localStore.put("recipes_get", {
        key: entry.key,
        value: { ...entry.value, items: next },
        fetchedAt: Date.now(),
      }).catch(() => {});
      // Drop itemCount on the corresponding list row too.
      const recipeId = entry.value?.recipe?.id;
      if (recipeId != null) {
        await patch<AnyRecord>("recipes_list", "_", (cur) => {
          if (!cur) return null;
          const recipes = (cur.recipes || []).map((r: AnyRecord) =>
            String(r.id) === String(recipeId)
              ? { ...r, itemCount: Math.max(0, (r.itemCount || 1) - 1) }
              : r
          );
          return { ...cur, recipes };
        });
      }
      break;
    }
  },
  // recipes.addToList writes into shopping items, not recipes — leave
  // the shopping cache mutators to handle that. No-op here.

  // ── Pantry ─────────────────────────────────────────────────────
  // pantry_list is a singleton keyed "_"; every mutation patches it
  // in place so the Pantry view reflects the change before the
  // server SSE event lands.
  "pantry.add": async (_args, resp) => {
    if (!resp?.item) return;
    await patch<AnyRecord>("pantry_list", "_", (cur) => {
      const items = cur?.items || [];
      return { ...(cur || { ok: true }), items: [...items, resp.item] };
    });
  },
  "pantry.update": async (args, resp) => {
    const item = resp?.item || { id: args.itemId, ...args };
    await patch<AnyRecord>("pantry_list", "_", (cur) => {
      if (!cur) return null;
      const items = (cur.items || []).map((it: AnyRecord) =>
        String(it.id) === String(args.itemId) ? { ...it, ...item } : it
      );
      return { ...cur, items };
    });
  },
  "pantry.delete": async (args) => {
    await patch<AnyRecord>("pantry_list", "_", (cur) => {
      if (!cur) return null;
      return { ...cur, items: (cur.items || []).filter((it: AnyRecord) => String(it.id) !== String(args.itemId)) };
    });
  },
};

export function lookupMutator(method: string): CacheMutator | null {
  return CACHE_MUTATORS[method] || null;
}
